import { getProfileData, getProfileFollowers, getProfileFollowing, updateFollowUserState, uploadProfilePhoto, getProfileLogs, getProfileLogsCSV } from "@/services/ProfileService"
import { formatDateToDDMMYYYY } from "@/utils/formatters"

const state = {
    profileData: {
        userName: null,
        created_at: null,
        avatar: null,
        followers: 0,
        following: 0,
        userRole: null,
        userId: null,
        resume: null,
        profilePicture: null
    },
    profileLogs: {
        data: [],
        currentPage: 1,
        lastPage: 1,
        search: '',
        startDate: '',
        endDate: ''
    },
    socialData: {
        followers: [],
        following: []
    },
    profileContacts: [],
    profileBadges: [],
    profileCardData: [],
    profileMetrics: {},
    lastRouteAccessed: null,
    userHasAccessedOwnProfile: false,
    profileSettingsSection: 'contactsSection'
}

const getters = {
    getExistsDataRelatedToProfileRoutment: (state) => Boolean(state.lastRouteAccessed || state.userHasAccessedOwnProfile),
    getUserLastRouteAcessedBeforeProfile: (state) => state.lastRouteAccessed,
    getUserHasAccessedOwnProfile: (state) => state.userHasAccessedOwnProfile,
    getProfileContacts: (state) => state.profileContacts,
    getProfileLastRouteAccessed: (state) => state?.lastRouteAccessed,
    getProfileMetrics: (state) => state?.profileMetrics,
    getProfileUserId: (state) => state?.profileData?.userId,
    getProfileRole: (state) => state?.profileData?.userRole,
    getProfileCardData: (state) => state?.profileCardData,
    getProfileData: (state) => state?.profileData,
    getProfileBadges: (state) => state?.profileBadges,
    getProfileFollowers: (state) => state.socialData?.followers,
    getProfileFollowing: (state) => state.socialData?.following,
    getProfilePicture: (state) => state?.profileData?.profilePicture,
    getProfileLogs: (state) => state.profileLogs,
    getProfileSettingsSection: (state) => state.profileSettingsSection
}

const mutations = {
    setDataRelatedToProfileRoutment(state, data) {
        state.lastRouteAccessed = data?.lastRouteAccessed || null
        state.userHasAccessedOwnProfile = data?.userHasAccessedOwnProfile || false
    },

    setProfileMetrics(state, data) {
        if (!data) return
        state.profileMetrics.hours_metrics = data?.hours_metrics ?
            !Array.isArray(data.hours_metrics) ? data.hours_metrics : null
            : null
        state.profileMetrics.points_metrics = data?.points_metrics ?
            !Array.isArray(data.points_metrics) ? data.points_metrics : null
            : null
    },

    setProfileData(state, data) {
        if (!data) return
        data.session = Array.isArray(data.session) && data.session[0] ?
            data.session[0]
            : null

        state.profileData.userId = data?.id || null
        state.profileData.userName = data?.name || 'User Name'
        state.profileData.avatar = data?.avatar || null
        state.profileData.followers = data?.followers || 0
        state.profileData.following = data?.following || 0
        state.profileBadges = data?.session?.badges || []
        state.profileData.created_at = data?.session?.user?.created_at || null
        state.profileData.userRole = data?.type || null
        state.profileData.resume = data?.resume || null
        state.profileData.profilePicture = data?.profile_picture || null

        let contactsData = []
        const contactsFromAPI = data?.contacts || []
        if (Array.isArray(contactsFromAPI) && contactsFromAPI.length > 0) {
            contactsData = contactsFromAPI.map((contact) => {
                return {
                    type: contact?.type || "Tipo do Contato",
                    value: contact?.type == 'email' ? contact.email : contact.phone_number
                }
            })
        }

        if (data.city) {
            contactsData = [...contactsData, { type: 'cidade', value: data?.city || null }]
        }

        state.profileContacts = contactsData

        switch (state.profileData.userRole) {
            case 'student':
                const studentTotalHours = data?.session?.total_time ?
                    (Number(data?.session?.total_time) / 3600).toFixed(1)
                    : 0

                const studentDisciplines = data?.inscriptions || []
                const studentEnrollments = Array.isArray(data?.enrollments) ? data.enrollments : []

                state.profileCardData = [
                    { value: data?.points || 0, title: 'Total de XP', imagePath: require('../assets/images/StarIcon.svg') },
                    { value: studentTotalHours, title: 'Horas Estudadas', imagePath: require('../assets/images/Relogio1.svg') },
                    { value: `${data?.overall_progress || '0.00'}%`, title: 'Progresso', imagePath: require('../assets/images/Thunder.svg') },
                    { value: state.profileBadges.length, title: 'Conquistas', imagePath: require('../assets/images/Conquistas.svg') },
                    { value: studentEnrollments.length, title: 'Matrículas', imagePath: require('../assets/images/Student2.svg'), metaData: studentEnrollments },
                    { value: studentDisciplines.length, title: 'Disciplinas', imagePath: require('../assets/images/Light.svg'), metaData: studentDisciplines },
                    { value: `${data?.academic_performance?.percentage ?? 0}%`, title: 'Desempenho', imagePath: require('../assets/images/Star.svg'), metaData: data?.academic_performance || null },
                    { value: formatDateToDDMMYYYY(data?.last_activity), title: 'Último acesso', imagePath: require('../assets/images/Relogio2.svg') }
                ]
                break;
            case 'tutor':
            case 'teacher':

                const totalHours = data?.session?.total_time ?
                    (Number(data.session.total_time) / 3600).toFixed(1)
                    : 0
                const totalHoursWeek = data?.session?.current_week ?
                    (Number(data.session.current_week) / 3600).toFixed(1)
                    : 0
                const teacherDisciplines = Array.isArray(data?.inscriptions)
                    ? data.inscriptions
                    : []

                state.profileCardData = [
                    { value: totalHours, title: 'Total de Horas', imagePath: require('../assets/images/Relogio1.svg') },
                    { value: data?.students_count || 0, title: 'Alunos ', imagePath: require('../assets/images/Estrela.svg') },
                    { value: totalHoursWeek, title: 'Horas da Semana', imagePath: require('../assets/images/Relogio2.svg') },
                    { value: data?.disciplines_count || 0, title: 'Disciplinas', imagePath: require('../assets/images/Lâmpada.svg'), metaData: teacherDisciplines }
                ]
                break;
            default:
                state.profileCardData = []
        }
    },

    setFollowersData(state, data) {
        if (data) {
            const typeDataIsArray = Array.isArray(data)
            state.socialData.followers = typeDataIsArray ? data : []
            state.profileData.followers = typeDataIsArray ?
                data.length
                : 0
        }
    },

    setFollowingData(state, data) {
        if (data) {
            const typeDataIsArray = Array.isArray(data)
            state.socialData.following = typeDataIsArray ? data : []
            state.profileData.following = typeDataIsArray ?
                data.length
                : 0
        }
    },

    setProfileSettingsSection(state, section) {
        state.profileSettingsSection = section || 'contactsSection'
    },

    setProfilePicture(state, url) {
        state.profileData.profilePicture = url || null
    },

    setProfileLogsData(state, payload) {
        state.profileLogs.data = Array.isArray(payload?.data) ? payload.data : []
        state.profileLogs.currentPage = payload?.meta?.current_page ?? payload?.current_page ?? 1
        state.profileLogs.lastPage = payload?.meta?.last_page ?? payload?.last_page ?? 1
        state.profileLogs.search = payload?.search || ''
        state.profileLogs.startDate = payload?.startDate || ''
        state.profileLogs.endDate = payload?.endDate || ''
    },

    resetProfileLogs(state) {
        state.profileLogs = {
            data: [],
            currentPage: 1,
            lastPage: 1,
            search: '',
            startDate: '',
            endDate: ''
        }
    },

    updateFollowUserState(state, data) {
        if (!data) return

        if (data.following) {
            state.socialData.followers.push({ id: data.userId, avatar: data.userAvatar })
            state.profileData.followers += 1
            return
        }

        state.profileData.followers -= 1
        const userIndexInProfile = state.socialData.followers
            .findIndex((userFollowing) => userFollowing.id === data.userId)
        userIndexInProfile !== -1 && (state.socialData.followers.splice(userIndexInProfile, 1))
    }
}

const actions = {
    setDataRelatedToProfileRoutment({ commit }, payload) {
        commit('setDataRelatedToProfileRoutment', payload)
    },

    setProfileSettingsSection({ commit }, section) {
        commit('setProfileSettingsSection', section)
    },

    async getProfileData({ commit, dispatch }, payload) {
        try {
            const response = await getProfileData(payload || null)
            commit('setProfileData', response?.data || null)
            commit('setProfileMetrics', response?.metrics || null)
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        }
    },

    async getProfileFollowers({ commit, dispatch }, payload) {
        try {
            const response = await getProfileFollowers(payload || null)
            commit('setFollowersData', response?.data || null)
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        }
    },

    async getProfileFollowing({ commit, dispatch }, payload) {
        try {
            const response = await getProfileFollowing(payload || null)
            commit('setFollowingData', response?.data || null)
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        }
    },

    async updateFollowUserState({ commit, dispatch }, payload) {
        if (!payload?.profileId) return

        try {
            const response = await updateFollowUserState(payload.profileId)
            commit('updateFollowUserState', { following: response?.data?.following, ...payload })
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        }
    },

    async uploadProfilePhoto({ commit, dispatch }, payload) {
        if (!payload?.userId || !payload?.photo) return false

        dispatch('skeleton/setLoadingState', { key: 'profilePhotoUpload', value: true })
        dispatch('toastfy/activeInfoNotification', { text: 'Enviando foto de perfil...' })
        try {
            const response = await uploadProfilePhoto(payload)
            commit('setProfilePicture', response?.data?.profile_picture || null)
            dispatch('toastfy/activeSuccesNotification', { text: 'Foto de perfil atualizada com sucesso!' })
            return true
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
            return false
        } finally {
            dispatch('skeleton/setLoadingState', { key: 'profilePhotoUpload', value: false })
        }
    },

    async getProfileLogs({ commit, dispatch }, payload) {
        if (!payload?.userId) return

        dispatch('skeleton/setLoadingState', { key: 'profileLogs', value: true })
        try {
            const response = await getProfileLogs(payload)
            commit('setProfileLogsData', {
                ...response,
                search: payload?.search || '',
                startDate: payload?.startDate || '',
                endDate: payload?.endDate || ''
            })
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        } finally {
            dispatch('skeleton/setLoadingState', { key: 'profileLogs', value: false })
        }
    },

    async downloadProfileLogsCSV({ dispatch }, userId) {
        if (!userId) return

        try {
            const response = await getProfileLogsCSV(userId)
            response?.url && window.open(response.url, '_blank')
        } catch (error) {
            dispatch('toastfy/activeErrorNotification', { text: error })
        }
    },

    resetProfileLogs({ commit }) {
        commit('resetProfileLogs')
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}